// src/pages/shop/Rewards.tsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  CardGiftcard as GiftIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import rewardService, { Reward } from "../../services/reward.service";
import ProtectedLayout from "../../components/common/ProtectedLayout";
const Rewards: React.FC = () => {
  const { user } = useAuth();
  const shopId = user?.shopId;
  const isAdmin = user?.role === "shop_admin";

  // Állapotok
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog állapotok
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [saving, setSaving] = useState(false);

  // Form állapotok
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPoints, setFormPoints] = useState<number | "">("");
  const [formActive, setFormActive] = useState(true);

  // Törlés dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rewardToDelete, setRewardToDelete] = useState<Reward | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Jutalmak betöltése
  const loadRewards = React.useCallback(async () => {
    if (!shopId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await rewardService.getShopRewards(shopId, false);
      setRewards(data);
    } catch (err: any) {
      setError(err.response?.data?.msg || "Hiba a jutalmak betöltésekor.");
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    loadRewards();
  }, [loadRewards]);

  // Dialog megnyitása új jutalomhoz
  const handleOpenCreate = () => {
    setDialogMode("create");
    setSelectedReward(null);
    setFormName("");
    setFormDescription("");
    setFormPoints("");
    setFormActive(true);
    setDialogOpen(true);
  };

  // Dialog megnyitása szerkesztéshez
  const handleOpenEdit = (reward: Reward) => {
    setDialogMode("edit");
    setSelectedReward(reward);
    setFormName(reward.name);
    setFormDescription(reward.description || "");
    setFormPoints(reward.pointsRequired);
    setFormActive(reward.isActive);
    setDialogOpen(true);
  };

  // Dialog bezárása
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReward(null);
  };

  // Mentés (létrehozás vagy szerkesztés)
  const handleSave = async () => {
    if (!shopId || !formName || !formPoints) return;

    try {
      setSaving(true);

      if (dialogMode === "create") {
        await rewardService.createReward(shopId, {
          name: formName,
          description: formDescription || undefined,
          pointsRequired: Number(formPoints),
          isActive: formActive,
        });
      } else if (selectedReward) {
        await rewardService.updateReward(selectedReward._id, {
          name: formName,
          description: formDescription || undefined,
          pointsRequired: Number(formPoints),
          isActive: formActive,
        });
      }

      handleCloseDialog();
      loadRewards();
    } catch (err: any) {
      setError(err.response?.data?.msg || "Hiba a mentés során.");
    } finally {
      setSaving(false);
    }
  };

  // Törlés dialog megnyitása
  const handleOpenDelete = (reward: Reward) => {
    setRewardToDelete(reward);
    setDeleteDialogOpen(true);
  };

  // Törlés végrehajtása
  const handleDelete = async () => {
    if (!rewardToDelete) return;

    try {
      setDeleting(true);
      await rewardService.deleteReward(rewardToDelete._id);
      setDeleteDialogOpen(false);
      setRewardToDelete(null);
      loadRewards();
    } catch (err: any) {
      setError(err.response?.data?.msg || "Hiba a törlés során.");
    } finally {
      setDeleting(false);
    }
  };

  // Loading állapot
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ProtectedLayout>
      <Box sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Jutalmak kezelése
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Hozz létre és kezelj jutalmakat a vásárlóid számára.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Új jutalom
          </Button>
        </Box>

        {/* Hiba üzenet */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Jutalmak listája */}
        {rewards.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <GiftIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Még nincsenek jutalmak
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hozz létre az első jutalmat a vásárlóid számára!
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              Első jutalom létrehozása
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {rewards.map((reward) => (
              <Card key={reward._id} elevation={2}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={(theme) => ({
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          bgcolor: reward.isActive
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(33, 150, 243, 0.3)'
                              : 'primary.light'
                            : theme.palette.mode === 'dark'
                              ? 'rgba(158, 158, 158, 0.2)'
                              : 'grey.300',
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        })}
                      >
                        <GiftIcon
                          sx={(theme) => ({
                            color: reward.isActive
                              ? theme.palette.mode === 'dark'
                                ? '#64b5f6'
                                : 'primary.main'
                              : theme.palette.mode === 'dark'
                                ? '#9e9e9e'
                                : 'grey.500',
                          })}
                        />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {reward.name}
                        </Typography>
                        {reward.description && (
                          <Typography variant="body2" color="text.secondary">
                            {reward.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label={reward.isActive ? "Aktív" : "Inaktív"}
                      color={reward.isActive ? "success" : "default"}
                      size="small"
                      sx={(theme) => {
                        if (theme.palette.mode === 'dark') {
                          return reward.isActive
                            ? {
                              bgcolor: 'rgba(102, 187, 106, 0.25)',
                              color: '#a5d6a7',
                              border: '1px solid rgba(102, 187, 106, 0.5)',
                            }
                            : {
                              bgcolor: 'rgba(158, 158, 158, 0.2)',
                              color: '#bdbdbd',
                              border: '1px solid rgba(158, 158, 158, 0.3)',
                            };
                        }
                        return {};
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <StarIcon sx={{ color: "warning.main", fontSize: 20 }} />
                    <Typography variant="body1" fontWeight="bold">
                      {reward.pointsRequired} pont
                    </Typography>
                  </Box>
                </CardContent>

                <Divider />

                {isAdmin && (
                  <CardActions sx={{ justifyContent: "flex-end" }}>
                    <IconButton
                      onClick={() => handleOpenEdit(reward)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDelete(reward)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                )}
              </Card>
            ))}
          </Box>
        )}

        {/* Létrehozás / Szerkesztés Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {dialogMode === "create"
              ? "Új jutalom létrehozása"
              : "Jutalom szerkesztése"}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}
            >
              <TextField
                label="Jutalom neve"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Pl.: Ingyen kávé"
                fullWidth
                required
              />
              <TextField
                label="Leírás (opcionális)"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Pl.: Bármilyen normál méretű kávé"
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="Szükséges pontok"
                type="number"
                value={formPoints}
                onChange={(e) =>
                  setFormPoints(e.target.value ? Number(e.target.value) : "")
                }
                placeholder="100"
                fullWidth
                required
                inputProps={{ min: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formActive}
                    onChange={(e) => setFormActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Aktív (látható a vásárlóknak)"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Mégse
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formName || !formPoints}
            >
              {saving ? <CircularProgress size={20} /> : "Mentés"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Törlés megerősítő Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Jutalom törlése</DialogTitle>
          <DialogContent>
            <Typography>
              Biztosan törölni szeretnéd a "{rewardToDelete?.name}" jutalmat?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ez a művelet nem vonható vissza.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Mégse
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={20} /> : "Törlés"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ProtectedLayout>
  );
};

export default Rewards;
